import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTE_TRANSLATIONS } from './data/constants';
import type { GalleryTab } from './data/gallery';

interface GalleryStateContextValue {
  activeGalleryTab: GalleryTab;
  setActiveGalleryTab: (tab: GalleryTab) => void;
}

const GalleryStateContext = createContext<GalleryStateContextValue | null>(null);

const getPageId = (pathname: string) => {
  const pathWithoutLanguage = pathname.replace(/^\/(ro|en)(?=\/|$)/, '') || '/';

  for (const page of Object.keys(ROUTE_TRANSLATIONS.ro) as Array<keyof typeof ROUTE_TRANSLATIONS.ro>) {
    if (
      pathWithoutLanguage === ROUTE_TRANSLATIONS.ro[page] ||
      pathWithoutLanguage === ROUTE_TRANSLATIONS.en[page]
    ) {
      return page;
    }
  }

  return pathWithoutLanguage;
};

export const GalleryStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const pageId = getPageId(pathname);
  const previousPageId = useRef(pageId);
  const [activeGalleryTab, setActiveGalleryTab] = useState<GalleryTab>('exterior');

  useEffect(() => {
    if (previousPageId.current !== pageId) {
      setActiveGalleryTab('exterior');
      previousPageId.current = pageId;
    }
  }, [pageId]);

  return (
    <GalleryStateContext.Provider value={{ activeGalleryTab, setActiveGalleryTab }}>
      {children}
    </GalleryStateContext.Provider>
  );
};

export const useGalleryState = (): GalleryStateContextValue => {
  const context = useContext(GalleryStateContext);

  if (!context) {
    throw new Error('useGalleryState must be used within GalleryStateProvider');
  }

  return context;
};
